import { useState, useRef, useEffect } from "react";
import { X, ChevronDown, Plus, Check } from "lucide-react";

const MultiSelectTag = ({ label, options = [], selected = [], onChange, placeholder = "Select..." }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((s) => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const addNew = () => {
    const trimmed = search.trim();
    if (trimmed && !selected.includes(trimmed)) {
      onChange([...selected, trimmed]);
    }
    setSearch("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = search.trim();
      // If typed text matches an existing option, toggle it
      const match = filteredOptions.find((o) => o.toLowerCase() === trimmed.toLowerCase());
      if (match) {
        toggle(match);
        setSearch("");
      } else if (trimmed) {
        addNew();
      }
    }
    if (e.key === "Backspace" && search === "" && selected.length > 0) {
      onChange(selected.slice(0, -1));
    }
  };

  const allOptions = [...new Set([...options, ...selected])].sort();
  const filteredOptions = search
    ? allOptions.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : allOptions;
  const isNew = search.trim() && !allOptions.some((o) => o.toLowerCase() === search.trim().toLowerCase());

  return (
    <div ref={ref} className="relative">
      {/* Selected tags + input */}
      <div
        className="form-input min-h-[38px] flex flex-wrap items-center gap-1 cursor-text py-1.5 px-2"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {selected.map((val) => (
          <span
            key={val}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#ff3737]/15 text-[#ff3737] text-[11px] font-mono"
          >
            {val}
            <X
              className="w-3 h-3 cursor-pointer hover:text-white"
              onClick={(e) => { e.stopPropagation(); toggle(val); }}
            />
          </span>
        ))}
        <input
          ref={inputRef}
          value={search}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? placeholder : ""}
          className="bg-transparent border-none outline-none text-[#ebebeb] text-xs font-mono flex-1 min-w-[60px] placeholder:text-[#ebebeb]/30 p-0"
          data-testid={`multiselect-input-${label.toLowerCase()}`}
        />
        <ChevronDown className="w-3.5 h-3.5 text-[#ebebeb]/30 flex-shrink-0" />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-[#1a1c1e] border border-white/10 rounded-lg shadow-2xl max-h-48 overflow-y-auto">
          {filteredOptions.map((opt) => {
            const isSelected = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`flex items-center justify-between w-full px-3 py-2 text-xs font-mono transition-colors ${
                  isSelected ? "text-[#ff3737] bg-[#ff3737]/5" : "text-[#ebebeb]/70 hover:bg-white/5 hover:text-white"
                }`}
              >
                {opt}
                {isSelected && <Check className="w-3 h-3" />}
              </button>
            );
          })}
          {isNew && (
            <button
              type="button"
              onClick={addNew}
              className="flex items-center gap-2 w-full px-3 py-2 text-xs font-mono text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors border-t border-white/5"
            >
              <Plus className="w-3 h-3" /> Add "{search.trim()}"
            </button>
          )}
          {filteredOptions.length === 0 && !isNew && (
            <p className="px-3 py-2 text-xs font-mono text-[#ebebeb]/30">No options</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectTag;
